import { useEffect, useRef, useState } from "react";
import type {
  ConfigurationEditDraft,
  EditingSecret,
} from "@proof/runtime-config";
import { Button, Control, JsonEditor } from "./components";
import { instanceMessage, instanceRequest } from "./instance-api";
import { operatorNavigate } from "./OperatorAccess";
import { isConfigurationEditDraft } from "./configuration-edit-model";

interface EditView {
  expectedRevision: string;
  configuration: ConfigurationEditDraft;
}
export interface AppliedConfiguration {
  revision: string;
  publicOrigin: string;
  restartRequired: true;
}
function slots(value: unknown): EditingSecret[] {
  if (!value || typeof value !== "object") return [];
  if (
    "kind" in value &&
    ["retain", "replace"].includes(String(value.kind)) &&
    "slot" in value &&
    typeof value.slot === "string"
  )
    return [value as EditingSecret];
  return Object.values(value).flatMap(slots);
}
const replacement = (slot: string): EditingSecret => ({
  kind: "replace",
  slot,
});
export function OperatorConfiguration({
  onApplied,
}: {
  onApplied: (result: AppliedConfiguration) => void;
}) {
  const [view, setView] = useState<EditView>(),
    [config, setConfig] = useState<ConfigurationEditDraft>(),
    [values, setValues] = useState<Record<string, string>>({}),
    [password, setPassword] = useState(""),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [prepared, setPrepared] = useState(false),
    [approved, setApproved] = useState(false),
    [advanced, setAdvanced] = useState("");
  const active = useRef(false),
    controller = useRef<AbortController | undefined>(undefined);
  useEffect(() => {
    active.current = true;
    const request = new AbortController();
    controller.current = request;
    void instanceRequest<EditView>(
      "/api/operator/configuration",
      undefined,
      undefined,
      request.signal,
    )
      .then((result) => {
        if (!request.signal.aborted) {
          if (!isConfigurationEditDraft(result.configuration))
            throw new Error();
          setView(result);
          setConfig(result.configuration);
        }
      })
      .catch((cause) => {
        if (!request.signal.aborted) setError(instanceMessage(cause));
      });
    return () => {
      active.current = false;
      request.abort();
    };
  }, []);
  const invalidate = () => {
    setPrepared(false);
    setApproved(false);
    setError("");
  };
  const change = (next: ConfigurationEditDraft) => {
    invalidate();
    setConfig(next);
    setAdvanced("");
  };
  const secret = (slot: string, ref: EditingSecret) => {
    if (!config) return;
    const next = structuredClone(config),
      path = slot.split(".");
    let parent: any = next;
    for (const key of path.slice(0, -1)) parent = parent[key];
    parent[path.at(-1)!] = ref;
    change(next);
    setValues((previous) => {
      const copy = { ...previous };
      delete copy[slot];
      return copy;
    });
  };
  const submit = async (apply: boolean) => {
    if (!config || !view) return;
    if (
      !password ||
      slots(config).some((ref) => ref.kind === "replace" && !values[ref.slot])
    ) {
      setError(
        "Enter your current owner password and every replacement credential.",
      );
      return;
    }
    setBusy(true);
    setError("");
    const replacements = Object.fromEntries(
      slots(config)
        .filter((ref) => ref.kind === "replace")
        .map((ref) => [ref.slot, values[ref.slot]]),
    );
    try {
      const result = await instanceRequest<any>(
        `/api/operator/configuration/${apply ? "apply" : "prepare"}`,
        {
          expectedRevision: view.expectedRevision,
          configuration: config,
          replacements,
          currentPassword: password,
          ...(apply ? { approved } : {}),
        },
        undefined,
        controller.current?.signal,
      );
      if (!active.current) return;
      if (apply) {
        setPassword("");
        setValues({});
        onApplied(result as AppliedConfiguration);
      } else {
        const url = URL.createObjectURL(
          new Blob([JSON.stringify(result, null, 2)], {
            type: "application/json",
          }),
        );
        const link = document.createElement("a");
        link.href = url;
        link.download = "proof-instance-edit.private.json";
        document.body.append(link);
        link.click();
        link.remove();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
        setPrepared(true);
      }
    } catch (cause) {
      if (active.current) {
        setPrepared(false);
        setApproved(false);
        setError(instanceMessage(cause));
      }
    } finally {
      if (active.current) setBusy(false);
    }
  };
  return (
    <main className="instance-shell instance-edit">
      <header className="instance-brand">
        Proof Studio <span>Platform operator</span>
      </header>
      <section className="instance-panel">
        <div className="instance-heading">
          <h1>Instance configuration</h1>
          <Button onClick={() => operatorNavigate("/operator")}>
            Back to platform status
          </Button>
        </div>
        <p>
          Changes create an immutable revision. Drain API work before applying,
          then restart every API before reopening traffic.
        </p>
        {error && (
          <p role="alert" className="instance-error">
            {error}
          </p>
        )}
        {!config && !error && <p role="status">Loading saved settings…</p>}
        {config && view && (
          <>
            <p>
              Editing revision <code>{view.expectedRevision}</code>. Existing
              credentials and their file paths are hidden.
            </p>
            <fieldset disabled={busy}>
              <h2>Deployment and rendering</h2>
              <div className="control-grid">
                <Control label="Instance name">
                  <input
                    value={config.name}
                    maxLength={200}
                    onChange={(event) =>
                      change({ ...config, name: event.target.value })
                    }
                  />
                </Control>
                <Control label="Profile">
                  <select
                    value={config.profile}
                    onChange={(event) =>
                      change({
                        ...config,
                        profile: event.target.value as typeof config.profile,
                      })
                    }
                  >
                    <option value="development">Development</option>
                    <option value="production">Production</option>
                  </select>
                </Control>
                <Control label="Public origin">
                  <input
                    value={config.http.publicOrigin}
                    onChange={(event) =>
                      change({
                        ...config,
                        http: {
                          ...config.http,
                          publicOrigin: event.target.value,
                        },
                      })
                    }
                  />
                </Control>
                <Control label="Allowed embed origins">
                  <textarea
                    value={config.http.embedOrigins.join("\n")}
                    onChange={(event) =>
                      change({
                        ...config,
                        http: {
                          ...config.http,
                          embedOrigins: event.target.value
                            .split("\n")
                            .filter(Boolean),
                        },
                      })
                    }
                  />
                </Control>
                <Control label="Trusted proxy addresses">
                  <textarea
                    value={config.http.trustedProxies.join("\n")}
                    onChange={(event) =>
                      change({
                        ...config,
                        http: {
                          ...config.http,
                          trustedProxies: event.target.value
                            .split("\n")
                            .filter(Boolean),
                        },
                      })
                    }
                  />
                </Control>
                <Control label="Render concurrency">
                  <input
                    type="number"
                    min={1}
                    max={32}
                    value={config.renderer.concurrency}
                    onChange={(event) =>
                      change({
                        ...config,
                        renderer: {
                          ...config.renderer,
                          concurrency: Number(event.target.value),
                        },
                      })
                    }
                  />
                </Control>
                <Control label="Worker lease in seconds">
                  <input
                    type="number"
                    min={1}
                    max={300}
                    value={config.renderer.leaseMs / 1000}
                    onChange={(event) =>
                      change({
                        ...config,
                        renderer: {
                          ...config.renderer,
                          leaseMs: Number(event.target.value) * 1000,
                        },
                      })
                    }
                  />
                </Control>
                <Control label="Session lifetime in minutes">
                  <input
                    type="number"
                    min={1}
                    max={480}
                    value={config.security.sessionMs / 60000}
                    onChange={(event) =>
                      change({
                        ...config,
                        security: {
                          ...config.security,
                          sessionMs: Number(event.target.value) * 60000,
                        },
                      })
                    }
                  />
                </Control>
                <Control label="Queue provider">
                  <select
                    value={config.queue.kind}
                    onChange={(event) =>
                      change({
                        ...config,
                        queue:
                          event.target.value === "redis"
                            ? { kind: "redis", url: replacement("queue.url") }
                            : { kind: "embedded" },
                      })
                    }
                  >
                    <option value="embedded">Embedded coordinator</option>
                    <option value="redis">Redis dispatch</option>
                  </select>
                </Control>
                {config.queue.kind === "redis" && (
                  <label>
                    <input
                      type="checkbox"
                      checked={!!config.queue.ca}
                      onChange={(event) => {
                        if (config.queue.kind === "redis") {
                          const queue = { ...config.queue };
                          if (event.target.checked)
                            queue.ca = replacement("queue.ca");
                          else delete queue.ca;
                          change({ ...config, queue });
                        }
                      }}
                    />{" "}
                    Use a private Redis CA certificate
                  </label>
                )}
              </div>
              <h2>Host integration</h2>
              <Control label="Identity provider">
                <select
                  value={config.identity.kind}
                  onChange={(event) =>
                    change({
                      ...config,
                      identity:
                        event.target.value === "http"
                          ? {
                              kind: "http",
                              endpoint: "",
                              token: replacement("identity.token"),
                              timeoutMs: 5000,
                              allowHttp: false,
                            }
                          : { kind: "development" },
                    })
                  }
                >
                  <option value="development">Development identity</option>
                  <option value="http">HTTP identity bridge</option>
                </select>
              </Control>
              {config.identity.kind === "http" && (
                <>
                  <Control label="Identity endpoint">
                    <input
                      value={config.identity.endpoint}
                      onChange={(event) => {
                        if (config.identity.kind === "http")
                          change({
                            ...config,
                            identity: {
                              ...config.identity,
                              endpoint: event.target.value,
                            },
                          });
                      }}
                    />
                  </Control>
                  <label>
                    <input
                      type="checkbox"
                      checked={config.identity.allowHttp}
                      onChange={(event) => {
                        if (config.identity.kind === "http")
                          change({
                            ...config,
                            identity: {
                              ...config.identity,
                              allowHttp: event.target.checked,
                            },
                          });
                      }}
                    />{" "}
                    Allow HTTP identity in development
                  </label>
                </>
              )}
              <label>
                <input
                  type="checkbox"
                  checked={!!config.context}
                  onChange={(event) => {
                    const next = { ...config };
                    if (event.target.checked)
                      next.context = {
                        endpoint: "",
                        token: replacement("context.token"),
                        timeoutMs: 5000,
                        allowHttp: false,
                      };
                    else delete next.context;
                    change(next);
                  }}
                />{" "}
                Use a profile/order context provider
              </label>
              {config.context && (
                <>
                  <Control label="Context endpoint">
                    <input
                      value={config.context.endpoint}
                      onChange={(event) =>
                        change({
                          ...config,
                          context: {
                            ...config.context!,
                            endpoint: event.target.value,
                          },
                        })
                      }
                    />
                  </Control>
                  <label>
                    <input
                      type="checkbox"
                      checked={config.context.allowHttp}
                      onChange={(event) =>
                        change({
                          ...config,
                          context: {
                            ...config.context!,
                            allowHttp: event.target.checked,
                          },
                        })
                      }
                    />{" "}
                    Allow HTTP context in development
                  </label>
                </>
              )}
              <h2>Order delivery</h2>
              <p>
                Connect approved packages to your ordering host. Validation
                checks status without submitting an order. HTTP tenant identity
                is required.
              </p>
              <label>
                <input
                  type="checkbox"
                  checked={!!config.delivery}
                  disabled={!config.delivery && config.identity.kind !== "http"}
                  onChange={(event) => {
                    const next = { ...config };
                    if (event.target.checked)
                      next.delivery = {
                        kind: "http",
                        id: "company-orders-v1",
                        label: "Ordering system",
                        endpoint: "",
                        token: replacement("delivery.token"),
                        timeoutMs: 4000,
                        allowHttp: false,
                      };
                    else delete next.delivery;
                    change(next);
                  }}
                />{" "}
                Use an ordering host
              </label>
              {config.delivery && (
                <div className="control-grid">
                  <Control
                    label="Ordering host ID"
                    note="Keep this identity stable for the host transaction ledger."
                  >
                    <input
                      value={config.delivery.id}
                      maxLength={200}
                      onChange={(event) =>
                        change({
                          ...config,
                          delivery: {
                            ...config.delivery!,
                            id: event.target.value,
                          },
                        })
                      }
                    />
                  </Control>
                  <Control label="Ordering display name">
                    <input
                      value={config.delivery.label}
                      maxLength={100}
                      onChange={(event) =>
                        change({
                          ...config,
                          delivery: {
                            ...config.delivery!,
                            label: event.target.value,
                          },
                        })
                      }
                    />
                  </Control>
                  <Control label="Ordering endpoint">
                    <input
                      value={config.delivery.endpoint}
                      maxLength={2048}
                      onChange={(event) =>
                        change({
                          ...config,
                          delivery: {
                            ...config.delivery!,
                            endpoint: event.target.value,
                          },
                        })
                      }
                    />
                  </Control>
                  <Control label="Ordering timeout (milliseconds)">
                    <input
                      type="number"
                      min={100}
                      max={5000}
                      value={config.delivery.timeoutMs}
                      onChange={(event) =>
                        change({
                          ...config,
                          delivery: {
                            ...config.delivery!,
                            timeoutMs: Number(event.target.value),
                          },
                        })
                      }
                    />
                  </Control>
                  <label>
                    <input
                      type="checkbox"
                      checked={config.delivery.allowHttp}
                      onChange={(event) =>
                        change({
                          ...config,
                          delivery: {
                            ...config.delivery!,
                            allowHttp: event.target.checked,
                          },
                        })
                      }
                    />{" "}
                    Allow HTTP ordering in development
                  </label>
                </div>
              )}
              <h2>Provider credentials</h2>
              <p>
                Leave existing credentials retained, or replace them with
                private values. Login and request-rate keys require a separate
                rotation procedure. Storage locations remain unchanged.
              </p>
              {slots(config)
                .filter((ref) => !ref.slot.startsWith("security."))
                .map((ref) => (
                  <div className="instance-credential" key={ref.slot}>
                    <Control label={`Credential action for ${ref.slot}`}>
                      <select
                        value={ref.kind}
                        onChange={(event) =>
                          secret(ref.slot, {
                            kind: event.target.value as EditingSecret["kind"],
                            slot: ref.slot,
                          })
                        }
                      >
                        {slots(view.configuration).some(
                          (existing) => existing.slot === ref.slot,
                        ) && (
                          <option value="retain">
                            Retain configured credential
                          </option>
                        )}
                        <option value="replace">Replace credential</option>
                      </select>
                    </Control>
                    {ref.kind === "replace" && (
                      <Control label={`Replacement for ${ref.slot}`}>
                        {ref.slot.endsWith(".ca") ? (
                          <textarea
                            value={values[ref.slot] || ""}
                            autoComplete="off"
                            spellCheck={false}
                            onChange={(event) => {
                              invalidate();
                              setValues({
                                ...values,
                                [ref.slot]: event.target.value,
                              });
                            }}
                          />
                        ) : (
                          <input
                            type="password"
                            autoComplete="off"
                            value={values[ref.slot] || ""}
                            onChange={(event) => {
                              invalidate();
                              setValues({
                                ...values,
                                [ref.slot]: event.target.value,
                              });
                            }}
                          />
                        )}
                      </Control>
                    )}
                  </div>
                ))}
              <details>
                <summary>Advanced operational settings</summary>
                <p>
                  Secret placeholders stay bound to their named positions. Use
                  the credential controls for replacements. Storage moves
                  require a separate migration.
                </p>
                <JsonEditor
                  label="Advanced configuration JSON"
                  value={config}
                  applyLabel="Use advanced settings"
                  onDirty={() => {
                    invalidate();
                    setAdvanced("pending");
                  }}
                  onSave={(parsed) => {
                    if (!isConfigurationEditDraft(parsed))
                      throw new Error(
                        "Enter a valid configuration object before applying advanced settings.",
                      );
                    change(parsed);
                  }}
                />
              </details>
              <h2>Review and apply</h2>
              <Control label="Current owner password">
                <input
                  type="password"
                  value={password}
                  maxLength={128}
                  autoComplete="current-password"
                  onChange={(event) => {
                    invalidate();
                    setPassword(event.target.value);
                  }}
                />
              </Control>
              <Button
                disabled={!password || !!advanced}
                onClick={() => void submit(false)}
              >
                {busy
                  ? "Checking settings…"
                  : "Validate and download private recovery file"}
              </Button>
              {prepared && (
                <>
                  <p role="status">
                    Connections validated. Keep the downloaded file private: it
                    contains your password, replacement credentials and
                    configuration references. Retry it with the offline edit
                    command if publication is interrupted.
                  </p>
                  <details>
                    <summary>Review candidate settings</summary>
                    <pre>{JSON.stringify(config, null, 2)}</pre>
                  </details>
                  <label>
                    <input
                      type="checkbox"
                      checked={approved}
                      onChange={(event) => setApproved(event.target.checked)}
                    />{" "}
                    I have retained the private recovery file, drained API work,
                    and will restart every API after applying.
                  </label>
                  <Button
                    kind="primary"
                    disabled={!approved}
                    onClick={() => void submit(true)}
                  >
                    Apply configuration and require restart
                  </Button>
                </>
              )}
            </fieldset>
          </>
        )}
      </section>
    </main>
  );
}
